// Payment methods, in one place. They were previously hardcoded in six components with four
// different value sets, which is how "Online" survived in some pickers after it was dropped from
// others.
//
// Two lists, because the two directions genuinely differ: the clinic takes CARD from a patient at
// the desk but never pays a supplier by card, and a vendor bill can be settled by cheque.

export const PAYMENT_MODE_LABEL = {
  CASH: 'Cash',
  CARD: 'Card',
  CHEQUE: 'Cheque',
  BANK_TRANSFER: 'Bank Transfer',
  MOBILE_WALLET: 'Mobile Wallet',
  SPLIT: 'Split',
  NONE: 'Not recorded',
  // Retired. Kept here so a record saved under one of them still displays its real method: the
  // values were only removed from the pickers, never from the data.
  //
  // JazzCash and EasyPaisa were replaced by the single Mobile Wallet: the clinic reconciles every
  // wallet the same way, and the screenshot names which one it was. No record had ever been saved
  // under either (checked on QA and local first), so nothing needed migrating.
  JAZZCASH: 'JazzCash',
  EASYPAISA: 'EasyPaisa',
  ONLINE: 'Online',
  CREDIT: 'Credit (pay later)',
  OTHER: 'Other',
};

/** No longer offered anywhere. Existing records keep them; nothing new can be saved with them. */
export const RETIRED_PAYMENT_MODES = new Set([
  'JAZZCASH', 'EASYPAISA', 'ONLINE', 'CREDIT', 'OTHER',
]);

const opt = (value) => ({ value, label: PAYMENT_MODE_LABEL[value] || value });

/** Money the clinic pays out: stock receipts, expenses, asset purchases. */
export const PAYMENT_MODES = ['CASH', 'CHEQUE', 'BANK_TRANSFER', 'MOBILE_WALLET'].map(opt);

/** Money the clinic takes in: patient invoices and token payments. */
export const PATIENT_PAYMENT_MODES = [
  'CASH', 'CARD', 'CHEQUE', 'BANK_TRANSFER', 'MOBILE_WALLET',
].map(opt);

/**
 * Methods that settle outside this system, so the money arriving is only evidenced by the transfer
 * screenshot. Cash and cheque leave their own trail; card and split are taken on the premises.
 * Mirrors PaymentProofService.PROOF_REQUIRED on the backend, which is what actually enforces it.
 */
// The retired wallet values stay listed: a record saved under one must not lose its proof
// requirement just because the picker moved on.
export const PROOF_REQUIRED_MODES = new Set([
  'BANK_TRANSFER', 'MOBILE_WALLET', 'JAZZCASH', 'EASYPAISA',
]);
export const paymentNeedsProof = (mode) => PROOF_REQUIRED_MODES.has(mode);

/**
 * The offered list plus, if the record being edited was saved under a method that is no longer
 * offered, that method — labelled as such. Without this, opening an old record silently rewrites its
 * payment method to whatever happened to be first in the dropdown.
 */
export const withRetiredMode = (options, current) => {
  if (!current || options.some(o => o.value === current)) return options;
  return [...options, { value: current, label: `${PAYMENT_MODE_LABEL[current] || current} (no longer offered)` }];
};
